import { useEffect, useState } from "react";

const ANNOUNCEMENT_ID = "aeo-citation-panel-operating-sheet-2026-10-29";
const DISMISSAL_KEY = `integrated_social_announcement_${ANNOUNCEMENT_ID}`;
const DISMISSAL_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

export const SITEWIDE_ANNOUNCEMENT = {
  id: ANNOUNCEMENT_ID,
  href: "/blog/aeo-citation-panel-operating-sheet-b2b-2026/",
  shortLabel: "New AEO guide",
  message:
    "New: build an AEO citation panel as an operating sheet, not a score.",
  ctaLabel: "Read the new guide",
} as const;

type SitewideAnnouncementBarProps = {
  onCtaClick: () => void;
  onDismiss: () => void;
  onVisibilityChange: (visible: boolean) => void;
};

/**
 * A small, dismissible sitewide editorial announcement. The dismissal is stored
 * only in the visitor's browser for seven days; it does not create a profile or
 * send any visitor data to the site.
 */
export default function SitewideAnnouncementBar({
  onCtaClick,
  onDismiss,
  onVisibilityChange,
}: SitewideAnnouncementBarProps) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    try {
      const dismissedAt = Number(window.localStorage.getItem(DISMISSAL_KEY));
      if (Number.isFinite(dismissedAt) && Date.now() - dismissedAt < DISMISSAL_DURATION_MS) {
        setVisible(false);
      } else {
        window.localStorage.removeItem(DISMISSAL_KEY);
      }
    } catch {
      // The bar remains visible when local storage is unavailable.
    }
  }, []);

  useEffect(() => {
    onVisibilityChange(visible);
  }, [onVisibilityChange, visible]);

  const dismiss = () => {
    try {
      window.localStorage.setItem(DISMISSAL_KEY, String(Date.now()));
    } catch {
      // Dismissing the visible bar must still work when local storage is blocked.
    }
    setVisible(false);
    onDismiss();
  };

  if (!visible) return null;

  return (
    <aside
      aria-label="Site announcement"
      data-announcement-id={SITEWIDE_ANNOUNCEMENT.id}
      style={{
        height: "44px",
        background: "#0B2730",
        borderBottom: "1px solid rgba(0, 212, 170, 0.24)",
        color: "white",
      }}
    >
      <div
        style={{
          maxWidth: "1280px",
          height: "44px",
          margin: "0 auto",
          padding: "0 10px 0 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "10px",
        }}
      >
        <span aria-hidden="true" style={{ color: "#00D4AA", fontSize: "12px", lineHeight: 1 }}>
          ●
        </span>
        <p style={{ margin: 0, minWidth: 0, overflow: "hidden", color: "rgba(255,255,255,0.92)", fontSize: "13px", lineHeight: 1.35, whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
          <span className="sm:hidden">{SITEWIDE_ANNOUNCEMENT.shortLabel}</span>
          <span className="hidden sm:inline">{SITEWIDE_ANNOUNCEMENT.message}</span>
        </p>
        <a
          href={SITEWIDE_ANNOUNCEMENT.href}
          onClick={onCtaClick}
          aria-label="Read the new guide: AEO Citation Panel: An Operating Sheet, Not a Score"
          style={{
            display: "inline-flex",
            alignItems: "center",
            minHeight: "30px",
            padding: "5px 8px",
            borderRadius: "4px",
            color: "#0F1419",
            background: "#00D4AA",
            fontSize: "12px",
            fontWeight: 800,
            lineHeight: 1,
            textDecoration: "none",
            whiteSpace: "nowrap",
          }}
        >
          <span className="sm:hidden">Read guide</span>
          <span className="hidden sm:inline">{SITEWIDE_ANNOUNCEMENT.ctaLabel} →</span>
        </a>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss site announcement for seven days"
          title="Dismiss for seven days"
          style={{
            width: "36px",
            height: "36px",
            marginLeft: "-2px",
            flex: "0 0 36px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            border: 0,
            borderRadius: "4px",
            background: "transparent",
            color: "white",
            cursor: "pointer",
            fontSize: "22px",
            lineHeight: 1,
          }}
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>
    </aside>
  );
}
